/**
 * DataListContext — React context для передачі `components`-оверрайдів
 * вглиб дерева (DataList → DataList.Item).
 *
 * Окремий файл виключає циклічну залежність між DataList.tsx та DataListItem.tsx.
 */

import { createContext, useContext, type ReactNode } from 'react'
import type React from 'react'

export interface DataListComponents {
    /**
     * Замінює кореневий <li> всередині DataList.Item.
     * Отримує ті самі props, що й стандартний <li>.
     * Корисно для інтеграції з dnd-kit, react-sortable тощо.
     */
    item?: React.ComponentType<React.HTMLAttributes<HTMLElement>>
    /**
     * Замінює дефолтний <Empty> при порожньому dataSource.
     * Компонент не отримує жодних props — весь контекст
     * закладається зовні через closure / власний стан.
     */
    empty?: React.ComponentType
    /**
     * Замінює <Spin> як обгортку при loading=true.
     * Отримує `spinning: boolean` та `children`.
     */
    loadingWrapper?: React.ComponentType<{ spinning: boolean; children: ReactNode }>
}

interface DataListContextValue {
    // Явно дозволяємо undefined (на відміну від optional `?`)
    // щоб уникнути проблем з exactOptionalPropertyTypes при передачі значення
    components: DataListComponents | undefined
}

export const DataListContext = createContext<DataListContextValue>({ components: undefined })

/** Хук для зчитування DataListContext всередині DataList.Item */
export const useDataListContext = () => useContext(DataListContext)
